/**
 * ErrorLog.tsx — Admin Panel "Error Log" tab
 *
 * Displays all invoices with ERROR or DUPLICATE status in a filterable,
 * sortable grid.  Each row links to the invoice for review/correction.
 *
 * Data source: GET /mgmt/error-log (joined with ap_invoice_history)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getErrorLog } from '../../adminApi';
import type { ErrorLogEntry } from '../../adminTypes';

const NAV_COLOR = '#00386C';

// ── Helpers ──────────────────────────────────────────────────────────────────

function toDateStr(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDollar(n: number | string): string {
  return '$' + Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Parse the error type from correction_notes or winteam_response */
function parseErrorType(notes: string | null, wtResp: any | null): string {
  if (notes) {
    if (notes.includes('Validation'))  return 'Validation';
    if (notes.includes('Duplicate'))   return 'Duplicate';
    if (notes.includes('Server Error')) return 'Server Error';
    const httpMatch = notes.match(/HTTP (\d+)/);
    if (httpMatch) return `HTTP ${httpMatch[1]}`;
    return 'Other';
  }
  // Fallback: parse from winteam_response (for older errors without correction_notes)
  if (wtResp?.errors?.length) return 'WinTeam Rejected';
  if (wtResp?.serverResponse) return 'WinTeam Error';
  return 'Unknown';
}

/** Strip the prefix tag from correction_notes for a cleaner display */
function stripPrefix(notes: string | null): string {
  if (!notes) return '';
  return notes.replace(/^\[WinTeam Upload Failed[^\]]*\]\s*/, '');
}

/** Extract a summary error message from the WinTeam response JSON */
function summarizeWtErrors(wtResp: any | null): string {
  if (!wtResp) return '';
  const errors = wtResp.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors
      .map((e: any) => `${e.fieldName || '?'}: ${e.errorMessage || '?'}`)
      .join('; ');
  }
  if (wtResp.serverResponse) return String(wtResp.serverResponse);
  return '';
}

// ── Component ────────────────────────────────────────────────────────────────

const ErrorLog: React.FC = () => {
  // Data
  const [entries, setEntries]       = useState<ErrorLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);

  // Filters
  const [fromDate, setFromDate]     = useState(toDateStr(30));
  const [toDate, setToDate]         = useState(toDateStr(0));
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]         = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Pagination
  const [page, setPage]             = useState(0);
  const PAGE_SIZE                   = 50;

  // Expanded row
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // ── Fetch data ───────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getErrorLog({
        from_date: fromDate || undefined,
        to_date:   toDate || undefined,
        status:    statusFilter || undefined,
        search:    search || undefined,
        limit:     PAGE_SIZE,
        offset:    page * PAGE_SIZE,
      });
      setEntries(result.entries);
      setTotalCount(result.total_count);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load error log');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, statusFilter, search, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Quick-select date presets ────────────────────────────────────────────
  const setDateRange = (days: number | null) => {
    if (days === null) {
      setFromDate('');
      setToDate('');
    } else {
      setFromDate(toDateStr(days));
      setToDate(toDateStr(0));
    }
    setPage(0);
  };

  // ── Search debounce ──────────────────────────────────────────────────────
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setSearch(searchInput);
      setPage(0);
    }
  };

  // ── Summary counts ───────────────────────────────────────────────────────
  const errorCount     = entries.filter(e => e.status === 'ERROR').length;
  const duplicateCount = entries.filter(e => e.status === 'DUPLICATE').length;
  const todayStr       = new Date().toISOString().slice(0, 10);
  const todayCount     = entries.filter(e => e.updated_at?.startsWith(todayStr)).length;

  // ── Styles ───────────────────────────────────────────────────────────────
  const cardStyle = (bg: string, color: string): React.CSSProperties => ({
    flex: 1,
    padding: '14px 18px',
    borderRadius: '8px',
    backgroundColor: bg,
    border: `1px solid ${color}20`,
  });

  const thStyle: React.CSSProperties = {
    padding: '10px 12px',
    fontSize: '12px',
    fontWeight: 700,
    textAlign: 'left',
    color: '#555',
    borderBottom: '2px solid #e0e0e0',
    backgroundColor: '#f8f9fa',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: '10px 12px',
    fontSize: '13px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'top',
  };

  return (
    <div>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>Error Log</h2>
        <span style={{
          backgroundColor: '#e8eaf6',
          color: NAV_COLOR,
          padding: '3px 10px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 700,
        }}>
          {totalCount} total
        </span>
        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            marginLeft: 'auto',
            padding: '7px 16px',
            fontSize: '13px',
            fontWeight: 600,
            border: `1px solid ${NAV_COLOR}`,
            borderRadius: '6px',
            backgroundColor: 'white',
            color: NAV_COLOR,
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* ── Summary cards ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '20px' }}>
        <div style={cardStyle('#fdecea', '#c0392b')}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#c0392b' }}>{errorCount}</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Errors</div>
        </div>
        <div style={cardStyle('#fff3e0', '#e67e22')}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#e67e22' }}>{duplicateCount}</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Duplicates</div>
        </div>
        <div style={cardStyle('#e3f2fd', '#1565c0')}>
          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1565c0' }}>{todayCount}</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Today</div>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '14px 16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
      }}>
        {/* Date range */}
        <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>From</label>
        <input
          type="date"
          value={fromDate}
          onChange={e => { setFromDate(e.target.value); setPage(0); }}
          style={{ padding: '5px 8px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '13px' }}
        />
        <label style={{ fontSize: '12px', fontWeight: 600, color: '#555' }}>To</label>
        <input
          type="date"
          value={toDate}
          onChange={e => { setToDate(e.target.value); setPage(0); }}
          style={{ padding: '5px 8px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '13px' }}
        />

        {/* Quick-select */}
        <div style={{ display: 'flex', gap: '4px', marginLeft: '4px' }}>
          {[
            { label: 'Today', days: 0 },
            { label: '7d', days: 7 },
            { label: '30d', days: 30 },
            { label: 'All', days: null },
          ].map(p => (
            <button
              key={p.label}
              onClick={() => setDateRange(p.days)}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                fontWeight: 600,
                border: '1px solid #cbd5e0',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: '#555',
                cursor: 'pointer',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div style={{ width: '1px', height: '24px', backgroundColor: '#cbd5e0' }} />

        {/* Status */}
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          style={{ padding: '5px 8px', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '13px' }}
        >
          <option value="">All Statuses</option>
          <option value="ERROR">ERROR</option>
          <option value="DUPLICATE">DUPLICATE</option>
        </select>

        {/* Search */}
        <input
          type="text"
          placeholder="Search tracking ID, invoice #, vendor, or error…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          onBlur={() => { if (searchInput !== search) { setSearch(searchInput); setPage(0); } }}
          style={{
            flex: 1,
            minWidth: '200px',
            padding: '5px 10px',
            borderRadius: '4px',
            border: '1px solid #cbd5e0',
            fontSize: '13px',
          }}
        />
      </div>

      {/* ── Error banner ────────────────────────────────────────────────── */}
      {error && (
        <div style={{
          backgroundColor: '#fdecea',
          color: '#c0392b',
          padding: '10px 14px',
          borderRadius: '6px',
          marginBottom: '16px',
          fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      {/* ── Data grid ───────────────────────────────────────────────────── */}
      <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Date/Time</th>
              <th style={thStyle}>Tracking ID</th>
              <th style={thStyle}>Invoice #</th>
              <th style={thStyle}>Vendor</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Error Type</th>
              <th style={{ ...thStyle, minWidth: '200px' }}>Error Message</th>
              <th style={thStyle}>Source</th>
            </tr>
          </thead>
          <tbody>
            {loading && entries.length === 0 && (
              <tr>
                <td colSpan={9} style={{ ...tdStyle, textAlign: 'center', padding: '40px', color: '#888' }}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && entries.length === 0 && (
              <tr>
                <td colSpan={9} style={{ ...tdStyle, textAlign: 'center', padding: '40px', color: '#888' }}>
                  No errors found for the selected filters.
                </td>
              </tr>
            )}
            {entries.map((e, i) => {
              const isExpanded = expandedId === e.tracking_id;
              const errorType  = parseErrorType(e.correction_notes, e.winteam_response);
              const errorMsg   = stripPrefix(e.correction_notes) || summarizeWtErrors(e.winteam_response) || '—';

              return (
                <React.Fragment key={e.tracking_id}>
                  <tr
                    onClick={() => setExpandedId(isExpanded ? null : e.tracking_id)}
                    style={{
                      backgroundColor: isExpanded ? '#f0f4ff' : i % 2 === 0 ? 'white' : '#fafafa',
                      cursor: 'pointer',
                      borderBottom: isExpanded ? 'none' : '1px solid #eee',
                    }}
                  >
                    <td style={tdStyle}>{fmtDateTime(e.updated_at)}</td>
                    <td style={tdStyle}>
                      <a
                        href={`/invoice?tracking_id=${e.tracking_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={ev => ev.stopPropagation()}
                        style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 600, fontSize: '12px' }}
                      >
                        {e.tracking_id}
                      </a>
                    </td>
                    <td style={tdStyle}>{e.invoice_number}</td>
                    <td style={tdStyle}>{e.vendor_name}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDollar(e.invoice_amount)}
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 700,
                        backgroundColor: e.status === 'ERROR' ? '#fdecea' : '#fff3e0',
                        color: e.status === 'ERROR' ? '#c0392b' : '#e67e22',
                      }}>
                        {e.status}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '12px', color: '#666' }}>{errorType}</span>
                    </td>
                    <td style={{
                      ...tdStyle,
                      maxWidth: '300px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: isExpanded ? 'normal' : 'nowrap',
                    }}>
                      {errorMsg}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '11px', color: '#888' }}>
                        {e.workflow_type || '—'}
                      </span>
                    </td>
                  </tr>

                  {/* Expanded detail row */}
                  {isExpanded && (
                    <tr style={{ backgroundColor: '#f0f4ff' }}>
                      <td colSpan={9} style={{ padding: '12px 20px 16px', borderBottom: '1px solid #ddd' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '13px' }}>
                          {/* Left column: error details */}
                          <div>
                            {e.correction_notes && (
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ fontWeight: 700, color: '#555', marginBottom: '4px', fontSize: '12px' }}>
                                  Error Notes
                                </div>
                                <div style={{
                                  backgroundColor: 'white',
                                  padding: '10px 12px',
                                  borderRadius: '6px',
                                  border: '1px solid #e0e0e0',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}>
                                  {e.correction_notes}
                                </div>
                              </div>
                            )}
                            {e.winteam_response?.errors && Array.isArray(e.winteam_response.errors) && (
                              <div>
                                <div style={{ fontWeight: 700, color: '#555', marginBottom: '4px', fontSize: '12px' }}>
                                  WinTeam Errors ({e.winteam_response.errors.length})
                                </div>
                                <div style={{
                                  backgroundColor: 'white',
                                  borderRadius: '6px',
                                  border: '1px solid #e0e0e0',
                                  overflow: 'hidden',
                                }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <thead>
                                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                                        <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', fontWeight: 600, color: '#555' }}>Field</th>
                                        <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', fontWeight: 600, color: '#555' }}>Error</th>
                                        <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', fontWeight: 600, color: '#555' }}>Value</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {e.winteam_response.errors.map((err: any, idx: number) => (
                                        <tr key={idx} style={{ borderBottom: idx < e.winteam_response.errors.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                          <td style={{ padding: '5px 10px', fontWeight: 600, color: '#c0392b', whiteSpace: 'nowrap' }}>{err.fieldName}</td>
                                          <td style={{ padding: '5px 10px' }}>{err.errorMessage}</td>
                                          <td style={{ padding: '5px 10px', color: '#888', fontFamily: 'monospace', fontSize: '11px' }}>{err.attemptedValue || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                            {!e.correction_notes && !e.winteam_response?.errors && (
                              <div style={{
                                backgroundColor: 'white',
                                padding: '10px 12px',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0',
                                color: '#888',
                              }}>
                                No error details available.
                              </div>
                            )}
                          </div>

                          {/* Right column: audit history */}
                          <div>
                            <div style={{ fontWeight: 700, color: '#555', marginBottom: '4px', fontSize: '12px' }}>
                              Audit History
                            </div>
                            <div style={{
                              backgroundColor: 'white',
                              padding: '10px 12px',
                              borderRadius: '6px',
                              border: '1px solid #e0e0e0',
                            }}>
                              {e.from_status && (
                                <div style={{ marginBottom: '4px' }}>
                                  <span style={{ color: '#888' }}>Status changed:</span>{' '}
                                  {e.from_status} → {e.status}
                                </div>
                              )}
                              {e.change_reason && (
                                <div style={{ marginBottom: '4px' }}>
                                  <span style={{ color: '#888' }}>Reason:</span>{' '}
                                  {e.change_reason}
                                </div>
                              )}
                              {e.source_filename && (
                                <div style={{ marginBottom: '4px' }}>
                                  <span style={{ color: '#888' }}>Source:</span>{' '}
                                  {e.source_filename}
                                </div>
                              )}
                              {e.winteam_response?.serverResponse && (
                                <div style={{ marginBottom: '4px' }}>
                                  <span style={{ color: '#888' }}>WinTeam:</span>{' '}
                                  <span style={{ color: '#c0392b' }}>{e.winteam_response.serverResponse}</span>
                                </div>
                              )}
                              {!e.from_status && !e.change_reason && !e.winteam_response?.serverResponse && '—'}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {totalCount > PAGE_SIZE && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '14px',
          fontSize: '13px',
          color: '#555',
        }}>
          <span>
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{
                padding: '5px 14px',
                fontSize: '12px',
                fontWeight: 600,
                border: '1px solid #cbd5e0',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: page === 0 ? 'default' : 'pointer',
                opacity: page === 0 ? 0.5 : 1,
              }}
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * PAGE_SIZE >= totalCount}
              style={{
                padding: '5px 14px',
                fontSize: '12px',
                fontWeight: 600,
                border: '1px solid #cbd5e0',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: (page + 1) * PAGE_SIZE >= totalCount ? 'default' : 'pointer',
                opacity: (page + 1) * PAGE_SIZE >= totalCount ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorLog;
